import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  CircularProgress,
  Slider,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { searchAPI, uploadAPI } from '../../services/api';
import SearchResults from './SearchResults';

const SearchInterface = () => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [topK, setTopK] = useState(5);

  // Check if user has connections
  const { data: uploadStatus } = useQuery({
    queryKey: ['uploadStatus'],
    queryFn: () => uploadAPI.getUploadStatus(),
    retry: 1,
  });

  // Get search suggestions
  const { data: suggestions } = useQuery({
    queryKey: ['searchSuggestions'],
    queryFn: () => searchAPI.getSearchSuggestions(),
    enabled: uploadStatus?.data?.has_connections,
    retry: 1,
  });

  // Search mutation
  const searchMutation = useMutation({
    mutationFn: ({ query, topK }) =>
      searchAPI.searchConnections(query, topK, false),
    onSuccess: (response) => {
      console.log('🔍 Frontend: Search success response:', response);
      console.log('🔍 Frontend: Response data:', response.data);
      console.log('🔍 Frontend: Results count:', response.data?.results?.length);
      
      setSearchResults(response.data);
      if (response.data.results.length === 0) {
        toast.info(`No matching connections found (${response.data.total_connections} total connections)`);
      } else {
        toast.success(`Found ${response.data.results.length} matches`);
      }
    },
    onError: (error) => {
      console.error('❌ Frontend: Search error:', error);
      console.error('❌ Frontend: Error response:', error.response);
      console.error('❌ Frontend: Error data:', error.response?.data);
      
      const message = error.response?.data?.error || 'Search failed';
      toast.error(message);
    },
  });

  const handleSearch = (searchQuery = query) => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    searchMutation.mutate({
      query: searchQuery.trim(),
      topK,
    });
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    handleSearch(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  const status = uploadStatus?.data;
  const hasConnections = status?.has_connections;
  const connectionCount = status?.connection_count || 0;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Search the Network
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Search across the entire network using natural language queries.
        {hasConnections ? ` You have ${connectionCount.toLocaleString()} connections uploaded.` : ' Upload your connections to add to the network!'}
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} mb={2}>
            <TextField
              fullWidth
              placeholder="e.g., software engineers at startups, data scientists, product managers..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={searchMutation.isPending}
              variant="outlined"
            />
            <Button
              variant="contained"
              startIcon={searchMutation.isPending ? <CircularProgress size={20} /> : <SearchIcon />}
              onClick={() => handleSearch()}
              disabled={searchMutation.isPending || !query.trim()}
              sx={{ minWidth: 120 }}
            >
              Search
            </Button>
          </Box>

          {/* Number of Results */}
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2">
              Number of results:
            </Typography>
            <Slider
              value={topK}
              onChange={(_, value) => setTopK(value)}
              min={1}
              max={20}
              marks={[
                { value: 5, label: '5' },
                { value: 10, label: '10' },
                { value: 20, label: '20' },
              ]}
              sx={{ width: 200 }}
            />
            <Typography variant="body2" color="primary">
              {topK}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Search Suggestions - Only show when no results */}
      {!searchResults && suggestions?.data?.suggestions?.common_patterns && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              💡 Try These Searches
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {suggestions.data.suggestions.common_patterns.slice(0, 6).map((suggestion, index) => (
                <Chip
                  key={index}
                  label={suggestion}
                  onClick={() => handleSuggestionClick(suggestion)}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Search Results */}
      {searchResults && (
        <SearchResults
          results={searchResults}
          loading={searchMutation.isPending}
          includeExplanations={false}
        />
      )}
    </Box>
  );
};

export default SearchInterface;